import { useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client"; 
import { getAllMatches, reorderMatches, moveCategory, addMatch, deleteMatch, updateSingleMatch, BASE_URL } from "../api/api.js";
import { Card, Button, Collapse, Modal, Form, Dropdown } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { FaFilter, FaSearch, FaChevronDown, FaChevronUp, FaPencilAlt, FaTrashAlt, FaPlus } from 'react-icons/fa';

// Definizione del componente della card Match (per una migliore leggibilità)
const MatchDisplayCard = ({ match, isAdmin, onEdit, onAddNext, getWinnerClass, dragHandleProps, draggableProps, innerRef, isDragDisabled }) => {
  return (
    <Card
      ref={innerRef}
      {...draggableProps}
      className={`mt-2 shadow-sm border-0 ${isDragDisabled ? 'bg-light' : ''}`}
    >
      <Card.Body className="p-2" {...dragHandleProps}>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <small className="fw-bold d-flex align-items-center">
            {/* Drag Handle a sinistra su mobile */}
            <span
              className="me-2 text-muted"
              style={{ cursor: isAdmin && !isDragDisabled ? "grab" : "not-allowed" }}
              title={!isAdmin ? "Solo Admin" : isDragDisabled ? "Match terminato" : "Trascina"}
            >
              ☰
            </span>
            Match {match.match_numero}
            {isAdmin && (<span className="text-muted ms-1">| {String(match.id).slice(0, 4)}</span>)}
          </small>
          {match.isCurrent && (<span className="badge bg-warning text-dark me-2">Corrente</span>)}
          {/* Azioni Admin a destra (Dropdown per compattare su mobile) */}
          {isAdmin && (
            <Dropdown>
              <Dropdown.Toggle variant="light" size="sm" id={`dropdown-admin-${match.id}`}>
                <FaPencilAlt size={12} />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => onEdit(match)} className="d-flex align-items-center">
                  <FaPencilAlt className="me-2" /> Modifica
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAddNext(match.match_numero)} className="d-flex align-items-center">
                  <FaPlus className="me-2" /> Aggiungi Successivo
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => onEdit(match, true)} // Aggiunto flag per l'eliminazione
                  className="d-flex align-items-center text-danger"
                >
                  <FaTrashAlt className="me-2" /> Elimina
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>

        <div className="row g-2 small">
          <div className="col-6">
            <div className={`p-2 rounded text-center ${getWinnerClass(match, 'R')}`}>
              <strong>{match.atleta_r}</strong><br />
              <small>{match.palestra_r}</small>
            </div>
          </div>
          <div className="col-6">
            <div className={`p-2 rounded text-center ${getWinnerClass(match, 'B')}`}>
              <strong>{match.atleta_b}</strong><br />
              <small>{match.palestra_b}</small>
            </div>
          </div>
          {(match.dettaglio || match.match_torneo) && (
            <div className="col-12 text-muted fst-italic mt-2">
              {match.match_torneo && <span>Torneo: {match.match_torneo}</span>}
              {match.dettaglio && <span>{match.match_torneo ? ' | ' : ''}Categoria: {match.dettaglio}</span>}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};


export default function MatchListPage({ isAdmin = false }) {
  // ❌ Rimosso UPDATE_INTERVAL, countdown

  const [areasData, setAreasData] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedArea, setSelectedArea] = useState("Tutte");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);

  const [showAddMatchModal, setShowAddMatchModal] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [editingMatch, setEditingMatch] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const emptyMatch = {
    match_numero: "",
    match_torneo: "",
    palestra_r: "",
    atleta_r: "",
    palestra_b: "",
    atleta_b: "",
    dettaglio: "",
    vincitore: null,
  };

  const [newMatch, setNewMatch] = useState(emptyMatch);
  const [openCategories, setOpenCategories] = useState({});
  const fetchedRef = useRef(false);

  // ➡️ Riferimento per l'istanza del socket
  const socketRef = useRef(null); 

  // Funzione per il ricaricamento dei dati (chiamata dall'evento Socket.IO)
  const reload = useCallback(async () => {
    try {
        const res = await getAllMatches();
        if (res.ok) {
            setAreasData(res.data);
        } else {
            console.error("Errore nel caricamento dei match:", res.error);
        }
    } catch (err) {
        console.error("Errore di rete/API durante il reload:", err);
    }
  }, []);

  // ➡️ useEffect per la Connessione Socket.IO (Sostituisce il polling)
  useEffect(() => {
    const url = BASE_URL 
    
    socketRef.current = io(url);

    // Gestione della connessione
    socketRef.current.on("connect", () => {
        console.log(`✅ Socket.IO Connesso! ID: ${socketRef.current.id}.`);
    });

    // Gestione degli errori (Utile per debugging)
    socketRef.current.on("connect_error", (err) => {
        console.error(`❌ Errore di connessione Socket.IO: ${err.message}. Verifica URL e Server Flask.`);
    });

    // Ascolto dell'evento di aggiornamento in tempo reale
    socketRef.current.on("refresh_matches", () => {
        console.log("➡️ Evento 'refresh_matches' ricevuto. Ricarico dati...");
        reload(); // Chiama la funzione di fetch dei match
    });

    // Cleanup: disconnessione quando il componente si smonta
    return () => {
        if (socketRef.current) {
            console.log("Cleanup: Disconnessione Socket.IO in corso.");
            socketRef.current.disconnect();
        }
    };
  }, [reload]); // Dipende da reload

  // useEffect per il caricamento iniziale (al montaggio)
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    // Chiamata iniziale al reload
    (async () => {
        await reload();
    })();
  }, [reload]);

  // Logica di filtro (invariata)
  useEffect(() => {
    let data = { ...areasData };
    if (selectedArea !== "Tutte") data = { [selectedArea]: data[selectedArea] || [] };
    if (selectedCategories.length) {
      Object.keys(data).forEach(area => {
        data[area] = data[area].filter(cat => selectedCategories.includes(cat.categoria));
      });
    }
    Object.keys(data).forEach(area => {
      data[area] = data[area]
        .map(cat => {
          let filteredMatches = cat.matches;
          if (searchText.trim()) {
            const s = searchText.toLowerCase();
            filteredMatches = cat.matches.filter(m =>
              `${m.match_numero} ${m.palestra_r} ${m.atleta_r} ${m.palestra_b} ${m.atleta_b} ${m.dettaglio || ""}`
                .toLowerCase()
                .includes(s)
            );
          }
          return { ...cat, matches: filteredMatches };
        })
        .filter(cat => cat.matches.length);
    });
    setFilteredData(Object.entries(data).filter(([, v]) => v.length));
  }, [areasData, selectedArea, selectedCategories, searchText]);

  const areas = ["Tutte", ...Object.keys(areasData)];
  const allCategories = Array.from(new Set(Object.values(areasData).flat().map(c => c.categoria)));

  // Drag and Drop Logic (modificato il reload finale)
  const onDragEnd = async ({ draggableId, source, destination, type }) => {
    if (!isAdmin || !destination) return;

    let success = true;

    // --- 1. Aggiornamento Ottimistico Dello Stato Locale (PRE-API) ---
    if (type === "match") {
      const sourceDroppableId = source.droppableId;
      const destDroppableId = destination.droppableId;
      if (sourceDroppableId !== destDroppableId) return;

      const newAreasData = { ...areasData };
      const categoriesInArea = Object.values(newAreasData).flat();
      const cat = categoriesInArea.find(c => c.id === sourceDroppableId);

      if (cat) {
        const newMatches = [...cat.matches];
        const [movedMatch] = newMatches.splice(source.index, 1);
        newMatches.splice(destination.index, 0, movedMatch);

        cat.matches = newMatches;
        setAreasData(newAreasData);

        const orderedIds = newMatches.map(m => m.id);

        try {
          // L'API chiamerà socketio.emit('refresh_matches') al successo
          await reorderMatches(cat.categoria, orderedIds);
        } catch (error) {
          console.error("Errore nel riordino del match, rollback:", error);
          success = false;
        }
      }
    } else if (type === "category") {
      const fromAreaId = Number(source.droppableId);
      const toAreaId = Number(destination.droppableId);

      try {
        await moveCategory({
          categoria: draggableId.split("|")[0],
          from_area: fromAreaId,
          to_area: toAreaId,
        });
      } catch (error) {
        console.error("Errore nello spostamento della categoria, rollback:", error);
        success = false;
      }
    }

    // --- 2. Ricaricamento Forzato / Rollback ---
    // In caso di errore, eseguiamo un rollback manuale.
    // In caso di successo, l'API del backend dovrebbe aver emesso l'evento
    // 'refresh_matches', ma chiamiamo reload() qui per un feedback immediato
    // per l'utente admin (aggiornamento ottimistico).
    if (!success) {
      reload(); // Rollback
    } else {
      // Lasciamo il reload qui per sicurezza post-drag, anche se Socket.IO lo farà
      reload(); 
    }
  };

  // Gestori Modificati per la UX Admin (Rimozione del reload post-successo)

  const handleOpenAddModal = (categoryId, matchToEdit = null, isDelete = false) => {
    setCurrentCategoryId(categoryId);
    setEditingMatch(matchToEdit);
    setIsDeleting(isDelete);
    setNewMatch(matchToEdit ? { ...matchToEdit } : emptyMatch);
    setShowAddMatchModal(true);
  };

  const handleAddMatch = async () => {
    await addMatch({ categoria: currentCategoryId, ...newMatch });
    handleCloseModal();
    // ➡️ L'aggiornamento è gestito dall'evento Socket.IO emesso dal backend!
  };

  const handleUpdateMatch = async () => {
    if (isDeleting) {
      if (!window.confirm("Eliminare definitivamente il match?")) return;
      await deleteMatch(editingMatch.id);
    } else {
      await updateSingleMatch(editingMatch.id, newMatch);
    }
    handleCloseModal();
    // ➡️ L'aggiornamento è gestito dall'evento Socket.IO emesso dal backend!
  };

  const handleCloseModal = () => {
    setShowAddMatchModal(false);
    setEditingMatch(null);
    setNewMatch(emptyMatch);
    setIsDeleting(false);
  };

  const getWinnerClass = (match, color) => {
    if (match.vincitore && match.vincitore === color)
      return `bg-success bg-opacity-25 text-dark border border-success shadow-lg`;
    if (match.vincitore)
      return `bg-light text-muted border border-${color === 'R' ? 'danger' : 'primary'}`;
    return `bg-${color === 'R' ? 'danger' : 'primary'} bg-opacity-10`;
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="container px-2 py-3">
        <span className="h5 mb-3 d-block">
          Match Live {isAdmin ? "(Admin)" : ""} - Aggiornamento: Real-Time
        </span>

        {/* FILTRI (LAYOUT MOBILE OPTIMIZED) - (invariati) */}
        <Form className="mb-4">
          <div className="row g-2">
            <div className="col-12 col-md-4">
              <Form.Select value={selectedArea} onChange={e => setSelectedArea(e.target.value)} size="sm">
                <option value="Tutte">Tutte le aree</option>
                {areas.filter(a => a !== "Tutte").map(a => <option key={a}>{a}</option>)}
              </Form.Select>
            </div>

            <div className="col-12 col-md-4">
              <Button
                variant="outline-secondary"
                className="w-100 d-flex justify-content-between align-items-center"
                size="sm"
                onClick={() => setShowCategoryFilter(v => !v)}
              >
                <FaFilter className="me-2" size={12} />
                Categorie ({selectedCategories.length || 'Tutte'})
                {showCategoryFilter ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              </Button>
              <Collapse in={showCategoryFilter}>
                <div className="border p-2 mt-2 shadow-sm rounded bg-white" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <small className="d-block mb-1 text-muted">Seleziona le categorie:</small>
                  {allCategories.map(cat => (
                    <Form.Check
                      key={cat}
                      label={<small>{cat}</small>}
                      checked={selectedCategories.includes(cat)}
                      onChange={() =>
                        setSelectedCategories(prev =>
                          prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
                        )
                      }
                    />
                  ))}
                </div>
              </Collapse>
            </div>

            <div className="col-12 col-md-4">
              <div className="input-group input-group-sm">
                <span className="input-group-text"><FaSearch size={12} /></span>
                <Form.Control
                  placeholder="Cerca atleta, palestra, match..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
              </div>
            </div>
          </div>
        </Form>

        {/* CONTENUTO (invariato) */}
        {filteredData.map(([areaName, categories]) => {
          const areaId = categories[0]?.area_id ?? 0;

          return (
            <div key={areaId} className="mb-3 border rounded shadow-sm">
              <div className="p-2 bg-light fw-bold small text-uppercase d-flex justify-content-between align-items-center">
                <span>Area: {areaName}</span>
                
              </div>
              <Droppable droppableId={String(areaId)} type="category">
                {p => (
                  <div ref={p.innerRef} {...p.droppableProps} className="p-2">
                    {categories.map((cat, i) => (
                      <Draggable
                        key={cat.id}
                        draggableId={String(cat.id)}
                        index={i}
                        isDragDisabled={!isAdmin}
                      >
                        {d => (
                          <div ref={d.innerRef} {...d.draggableProps} className="mb-2 border rounded">
                            <div
                              className="p-2 bg-light d-flex align-items-center cursor-pointer"
                              onClick={() => setOpenCategories(o => ({ ...o, [cat.id]: !o[cat.id] }))}
                            >
                              <span
                                {...d.dragHandleProps}
                                className="me-2 text-muted small"
                                style={{ cursor: isAdmin ? "grab" : "not-allowed" }}
                                title={isAdmin ? "Trascina categoria" : "Sposta categoria"}
                              >
                                ☰
                              </span>
                              <strong className="flex-grow-1 small">{cat.categoria}</strong>
                              <small className="text-muted me-2">{cat.matches.length} match</small>
                              {openCategories[cat.id] ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                            </div>
                            <Collapse in={openCategories[cat.id]}>
                              <div className="p-2 pt-1">
                                <Droppable droppableId={cat.id} type="match">
                                  {ip => (
                                    <div ref={ip.innerRef} {...ip.droppableProps}>
                                      {cat.matches.map((m, mi) => (
                                        <Draggable
                                          key={m.id}
                                          draggableId={String(m.id)}
                                          index={mi}
                                          isDragDisabled={!isAdmin || m.vincitore !== null || m.isCurrent}
                                        >
                                          {mp => (
                                            <MatchDisplayCard
                                              match={m}
                                              isAdmin={isAdmin}
                                              onEdit={(match, isDelete = false) => handleOpenAddModal(cat.categoria, match, isDelete)}
                                              onAddNext={(match_numero) => {
                                                handleOpenAddModal(cat.categoria, null);
                                                setNewMatch({ ...emptyMatch, match_numero: (parseInt(match_numero) + 1).toString() });
                                              }}
                                              getWinnerClass={getWinnerClass}
                                              dragHandleProps={mp.dragHandleProps}
                                              draggableProps={mp.draggableProps}
                                              innerRef={mp.innerRef}
                                              isDragDisabled={!isAdmin || m.vincitore !== null || m.isCurrent}
                                            />
                                          )}
                                        </Draggable>
                                      ))}
                                      {ip.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </div>
                            </Collapse>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {p.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}

        {/* MODAL MATCH (invariata) */}
        <Modal show={showAddMatchModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>{editingMatch ? (isDeleting ? "Elimina Match" : "Modifica Match") : "Nuovo Match"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {Object.keys(emptyMatch).map(k => (
              (k !== 'vincitore' || !editingMatch) && (
                <Form.Group key={k} className="mb-2">
                  <Form.Label><small className="text-muted">{k.replace(/_/g, ' ').toUpperCase()}</small></Form.Label>
                  <Form.Control
                    value={newMatch[k] ?? ""}
                    onChange={e => setNewMatch({ ...newMatch, [k]: e.target.value })}
                    disabled={isDeleting}
                    size="sm"
                  />
                </Form.Group>
              )
            ))}
            {isDeleting && <p className="text-danger mt-3">Confermi l'eliminazione del match {editingMatch?.match_numero}?</p>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Annulla</Button>
            <Button
              onClick={editingMatch ? handleUpdateMatch : handleAddMatch}
              variant={isDeleting ? "danger" : "primary"}
            >
              {isDeleting ? "Elimina Definitivamente" : "Salva"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </DragDropContext>
  );
}